import React, { useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { RefreshNavLink as NavLink } from '../../components/RefreshLink';

// Positions (dx = horizontal, dy = upward) for up to 4 radial buttons fanned in
// an arc above the center button. Computed on a ~118px radius, angles 150→30°.
const RADIAL_POS = [
  { dx: -102, dy: 59 },
  { dx: -40, dy: 111 },
  { dx: 40, dy: 111 },
  { dx: 102, dy: 59 },
];

// Barre du bas — Brand Kit §06 « Thème sombre mobile » (livre 2026-09-04) :
// fond #111827 (gray-900) bordé en haut #374151 (gray-700), padding 8/4/7 px,
// libellés 9.5 px #9CA3AF (gray-400) graisse normale, icônes 16 px, bouton
// central rond 46 px BLANC remonté de 26 px, icône encre 20 px, bordure 4 px
// #0A0A0A (night). Frame imposé : jamais recoloré. (Onglet actif = blanc :
// hors livre, gardé.)
export default function BottomNav({ tabs, onMore, onAction, moreActive, hidden }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [radialTab, setRadialTab] = useState(null); // center tab whose radial menu is open
  const pressTimer = useRef(null);
  const didLongPress = useRef(false);

  const base =
    'flex-1 flex flex-col items-center justify-center gap-0.5 py-2 text-[11px] font-normal transition-colors';
  const on = 'text-white';
  const off = 'text-gray-400';

  // Tabs that share a route (e.g. Ventes + Réservations both → /seller-sales)
  // supply an `activeMatch` to disambiguate the highlight; others fall back to
  // NavLink's path-based isActive.
  const resolveActive = (tab, navActive) =>
    tab.activeMatch ? tab.activeMatch(location) : navActive;

  // Long-press on a center button that has a `radial` menu.
  const startPress = (tab) => {
    if (!tab.radial) return;
    didLongPress.current = false;
    clearTimeout(pressTimer.current);
    pressTimer.current = setTimeout(() => {
      didLongPress.current = true;
      setRadialTab(tab);
    }, 450);
  };
  const cancelPress = () => clearTimeout(pressTimer.current);

  const centerTap = (tab) => {
    // A long-press just fired → swallow the tap that follows on release.
    if (didLongPress.current) { didLongPress.current = false; return; }
    navigate(tab.to, tab.state ? { state: tab.state } : undefined);
  };

  const goRadial = (item) => {
    setRadialTab(null);
    navigate(item.to, item.state ? { state: item.state } : undefined);
  };

  return (
    <>
      {/* Effet lumière du bouton central retiré (Ahmed 2026-08-16). */}
      <style>{`
        @keyframes kbnRadialIn { from { opacity: 0; transform: scale(.5); } to { opacity: 1; transform: scale(1); } }
      `}</style>

    {/* Radial long-press menu, rendered above the bar. */}
    {radialTab && (
      <div className="fixed inset-0 z-[60] bg-black/50" onClick={() => setRadialTab(null)}>
        <div className="absolute left-1/2 bottom-24" style={{ width: 0, height: 0 }}>
          {radialTab.radial.map((item, idx) => {
            const pos = RADIAL_POS[idx] || { dx: 0, dy: 0 };
            const RIcon = item.icon;
            return (
              // Wrapper handles the arc position; inner button animates in.
              <div
                key={idx}
                className="absolute left-0 bottom-0"
                style={{ transform: `translate(calc(-50% + ${pos.dx}px), ${-pos.dy}px)` }}
              >
                {/* Satellites du bouton central : même identité (rond blanc,
                    icône encre, bordure night). */}
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); goRadial(item); }}
                  aria-label={item.label}
                  title={item.label}
                  className="h-14 w-14 rounded-full bg-white text-gray-900 border-2 border-night shadow-lg flex items-center justify-center text-xl"
                  style={{ animation: 'kbnRadialIn .18s ease-out both', animationDelay: `${idx * 40}ms` }}
                >
                  <RIcon />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    )}

    <nav
      className={`fixed bottom-0 inset-x-0 z-40 flex bg-gray-900 border-t border-gray-700 transition-transform duration-300 ${
        hidden ? 'translate-y-full' : 'translate-y-0'
      }`}
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      {tabs.map((tab, i) => {
        const Icon = tab.icon;

        // Center tab (Ventes): same look as its neighbours since 2026-10-04.
        // Tap navigates; long-press opens its radial menu.
        if (tab.center) {
          const active = resolveActive(tab, location.pathname === tab.to) && !moreActive;
          return (
            <button
              key={i}
              type="button"
              onClick={() => centerTap(tab)}
              onPointerDown={() => startPress(tab)}
              onPointerUp={cancelPress}
              onPointerLeave={cancelPress}
              onContextMenu={(e) => e.preventDefault()}
              className={`${base} ${active ? on : off}`}
            >
              {/* Ahmed 2026-10-04 : Ventes = même design et même couleur que les
                  autres onglets de la ligne (plus de rond blanc surélevé) ;
                  l'appui long garde son menu radial. */}
              <Icon className="text-xl" />
              <span className="max-w-full truncate px-0.5">{tab.label}</span>
            </button>
          );
        }

        // Action tab (e.g. Paiement): opens a popup instead of navigating.
        if (tab.action) {
          return (
            <button
              key={i}
              onClick={() => onAction(tab.action)}
              className={`${base} ${off}`}
            >
              <Icon className="text-xl" />
              <span className="max-w-full truncate px-0.5">{tab.label}</span>
            </button>
          );
        }

        if (tab.more) {
          return (
            <button
              key={i}
              onClick={onMore}
              className={`${base} ${moreActive ? on : off}`}
            >
              <Icon className="text-xl" />
              <span className="max-w-full truncate px-0.5">{tab.label}</span>
            </button>
          );
        }
        return (
          <NavLink
            key={i}
            to={tab.to}
            state={tab.state}
            end={tab.to === '/'}
            className={({ isActive }) =>
              `${base} ${resolveActive(tab, isActive) && !moreActive ? on : off}`
            }
          >
            <Icon className="text-xl" />
            <span>{tab.label}</span>
          </NavLink>
        );
      })}
    </nav>
    </>
  );
}
